import { useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useReactTableState } from 'hooks/useReactTableState';
import useTableCurrentState from 'hooks/useTableCurrentState';

import { filterRowOptions } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal.utils';
import { TableRef } from 'components/common/Tables/TableV8/tableV8';
import { serializeColumnsVisibility } from 'components/common/Tables/Tables.utils';

import { useMutationSetWidgetColumns } from 'components/Dashboards/Project/Components/CustomDashboard/queries/useQuerySelectedTableColumns';

export type UseBaseTable = ReturnType<typeof useBaseTable>;

export const useBaseTable = ({
    widgetId,
    localFilters,
    componentKey,
    initialState,
    columns,
    initialPagination = { page: 1, size: 50 },
}) => {
    const { handleSnackBar } = useCustomSnackBar();
    const [totalPages, setTotalPages] = useState(0);
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const tableRef = useRef<TableRef<unknown>>(null);

    // react table v8 state
    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        columnFilters,
        setColumnFilters,
        rowSelection,
        setRowSelection,
    } = useReactTableState({
        initialSorting: widgetId ? localFilters?.sorting : initialState.sortBy,
        initialPagination,
    });

    // Initial State
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });

    // Table Instance
    const tableInstance = useMemo(() => {
        const hiddenColumns = Object.keys(columnVisibility).reduce((acc: string[], item: string) => {
            columnVisibility[item] === false && acc.push(item);
            return acc;
        }, []);

        const finalColumns = columnOrder && columnOrder.length ? columnOrder : columns.map((x) => x.id);

        const visibleColumnsOnly = columns
            .filter((item) => {
                return columnVisibility[item.id];
            })
            .filter(filterRowOptions)
            .map((item) => ({ ...item, Header: item.header }));

        return {
            setColumnOrder,
            setHiddenColumns: (hiddenColumns) =>
                setColumnVisibility(serializeColumnsVisibility({ columns: columns, hiddenColumns })),
            state: {
                hiddenColumns,
            },
            allColumns: finalColumns
                .map((item) => {
                    const column = columns.find((column) => column.id === item);
                    if (column && column.id) {
                        return {
                            ...column,
                            isVisible: !hiddenColumns.includes(column.id),
                        };
                    }
                    return null;
                })
                .filter((x) => x),
            sortedRows: tableRef.current?.table.getFilteredRowModel().rows.map((item) => ({
                values: { ...item.original },
            })),
            visibleColumns: visibleColumnsOnly,
        };
    }, [columnVisibility, columnOrder]);

    useEffect(() => {
        updatedInitialState && setColumnOrder(updatedInitialState.columnOrder ?? columns.map((item) => item.id));
        updatedInitialState &&
            setColumnVisibility(
                serializeColumnsVisibility({ columns: columns, hiddenColumns: updatedInitialState.hiddenColumns }) ??
                    {},
            );
    }, [updatedInitialState]);

    // set widget columns when adding widget to pinboard
    const setWidgetColumnsMutation = useMutationSetWidgetColumns();
    const setWidgetColumns = (widgetId: string) => {
        if (tableInstance) {
            setWidgetColumnsMutation.mutate(
                { columns: { hiddenColumns: tableInstance.state.hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    return {
        table: {
            pagination,
            setPagination,
            sorting,
            setSorting,
            totalPages,
            setTotalPages,
            tableInstance,
            updatedInitialState,
            setWidgetColumns,
            setColumnOrder,
            setColumnVisibility,
            columnVisibility,
            columnOrder,
            columnFilters,
            setColumnFilters,
            rowSelection,
            setRowSelection,
            modalColumnsOpen,
            setModalColumnsOpen,
            tableRef,
        },
    };
};
